import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { RmButton, RmEditorHeader, RmEditorPage, RmFooter } from '../RemindersEditor/EditorShell';
import { GAP_OFFER_PREVIEW, GAP_OFFER_STEPS, GAP_REPLIES } from '../../settings-gapfiller/mockData';
import type { GapOfferChannel, GapOfferStep, GapReplies } from '../../settings-gapfiller/types';

export interface GapOfferViewProps {
  page: 'messages' | 'test';
  onBack: () => void;
  onGoTo: (page: 'messages' | 'test') => void;
  onSave: () => void;
}

const CHANNEL_LABEL: Record<GapOfferChannel, string> = { text: 'Text', ai_call: 'AI call', email: 'Email' };
const CHANNEL_ICON: Record<GapOfferChannel, IconKind> = { text: 'chat', ai_call: 'phone', email: 'mail' };

/**
 * The offer — Messages and Test (amendment 26 §6, board `GF2Offer`).
 *
 * The offer is the reminders editor with two gap-specific additions the spec
 * calls for: an **Only if** box on a step (a call only if they haven't replied
 * and the gap is close), and the two **Replies** — what a patient hears when they
 * get it, and when someone else got it first. The *first yes books it* rule sits
 * under the steps as a plain sentence, because it is the whole behaviour.
 */
export function GapOfferView({ page, onBack, onGoTo, onSave }: GapOfferViewProps) {
  const [steps, setSteps] = useState<GapOfferStep[]>(GAP_OFFER_STEPS);
  const [selectedId, setSelectedId] = useState(steps[0]?.id);
  const [replies] = useState<GapReplies>(GAP_REPLIES);
  const selected = steps.find((s) => s.id === selectedId) ?? steps[0];

  if (page === 'test') {
    return (
      <RmEditorPage>
        <RmEditorHeader
          active="test"
          crumb="Settings › Gap Filler › The offer"
          titleLead="Check it, then"
          titleAccent="save"
          sub="See the offer on patients from a real open gap, and send it to yourself."
          onGoTo={(p) => { if (p === 'messages') onGoTo('messages'); }}
        />
        <div className="mt-5 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
          <div className="text-[12.5px] font-bold text-[var(--text-primary)]">How it reads</div>
          <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">Two patients from today’s 2:00 PM gap.</div>
          <div className="mt-3 grid grid-cols-2 gap-3 max-[900px]:grid-cols-1">
            {GAP_OFFER_PREVIEW.map((p) => (
              <div key={p.id} className="rounded-[10px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
                <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">{p.name}</div>
                <div className="text-[10.5px] text-[var(--text-muted)]">{p.why}</div>
                <div className="mt-1.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                  How the call opens
                </div>
                <p className="m-0 mt-1 text-[12px] leading-[1.7] text-[var(--text-secondary)]">“{p.spoken}”</p>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-[12px] bg-[var(--surface-sunken)] p-3.5">
            <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Send it to yourself</div>
            <p className="m-0 mt-1 text-[11px] leading-[1.55] text-[var(--text-muted)]">
              A made-up gap tomorrow at 10:00, marked [Test]. A test YES books nothing.
            </p>
            <div className="mt-2.5 flex gap-2">
              <RmButton>Send the text</RmButton>
              <RmButton kind="soft">Send every step</RmButton>
            </div>
          </div>
        </div>
        <RmFooter>
          <RmButton onClick={onBack}>Back</RmButton>
          <RmButton kind="primary" onClick={onSave}>Save changes</RmButton>
        </RmFooter>
      </RmEditorPage>
    );
  }

  return (
    <RmEditorPage>
      <RmEditorHeader
        active="messages"
        crumb="Settings › Gap Filler › The offer"
        titleLead="The"
        titleAccent="offer"
        sub="What each patient receives when a gap opens, and in what order. It stops as soon as someone says yes."
        onGoTo={(p) => { if (p === 'test') onGoTo('test'); }}
      />

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        {/* steps + replies */}
        <div className="w-[392px] shrink-0 max-[1100px]:w-full">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">The steps</div>
          <div className="mt-2.5 flex flex-col gap-2">
            {steps.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setSelectedId(s.id); }}
                className={cn(
                  'rounded-[12px] border p-3 text-left',
                  s.id === selected?.id
                    ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
                    : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]',
                )}
              >
                <span className="flex items-center gap-2">
                  <Icon kind={CHANNEL_ICON[s.channel]} size={13} stroke={2} className="text-[var(--text-muted)]" />
                  <span className="text-[12.5px] font-bold text-[var(--text-primary)]">{CHANNEL_LABEL[s.channel]}</span>
                  <span className="text-[11px] text-[var(--text-muted)]">{s.rightAway ? 'right away' : `${String(s.offsetMinutes)} min later`}</span>
                  {s.onlyIf?.noMobile === true ? (
                    <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">No mobile</span>
                  ) : null}
                  {s.onlyIf?.noReplyToText === true ? (
                    <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">If no reply</span>
                  ) : null}
                </span>
                <span className="mt-1 block truncate text-[11.5px] text-[var(--text-secondary)]">
                  {s.body !== '' ? s.body : (s.brief ?? '')}
                </span>
              </button>
            ))}
          </div>
          <button type="button" className="mt-2 w-full rounded-[12px] border border-dashed border-[var(--brand-indigo)] px-3 py-2.5 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]">
            + Add a step
          </button>

          <p className="m-0 mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            <b className="font-semibold text-[var(--text-secondary)]">The first yes books it.</b> Every other offer for
            that gap stops, and anyone who says yes after hears it’s gone.
          </p>

          {/* the two replies */}
          <div className="mt-4">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Replies</div>
            <ReplyRow title="When they get it" body={replies.whenTheyGetIt} />
            <ReplyRow title="When someone else got it first" body={replies.whenSomeoneElseGotItFirst} />
          </div>
        </div>

        {/* the editor for the selected step */}
        <div className="min-w-0 flex-1">
          {selected === undefined ? null : (
            <StepEditor
              step={selected}
              onChange={(next) => { setSteps(steps.map((s) => (s.id === next.id ? next : s))); }}
            />
          )}
        </div>
      </div>

      <RmFooter left={<span className="text-[12.5px] font-semibold text-[var(--money-green)]">Every step is ready.</span>}>
        <RmButton onClick={onBack}>Back to settings</RmButton>
        <RmButton kind="primary" onClick={() => { onGoTo('test'); }}>Continue to test</RmButton>
      </RmFooter>
    </RmEditorPage>
  );
}

function ReplyRow({ title, body }: { title: string; body: string }) {
  return (
    <div className="mt-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] p-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] font-semibold text-[var(--text-primary)]">{title}</span>
        <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Edit</button>
      </div>
      <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]">{body}</p>
    </div>
  );
}

function StepEditor({ step, onChange }: { step: GapOfferStep; onChange: (s: GapOfferStep) => void }) {
  const isCall = step.channel === 'ai_call';
  return (
    <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-5">
      <div className="text-[14px] font-bold text-[var(--text-primary)]">
        {CHANNEL_LABEL[step.channel]}
        {isCall ? ` · ${String(step.offsetMinutes)} minutes after the text` : step.rightAway ? ' · right away' : ''}
      </div>
      {isCall ? (
        <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">Within call hours. Never after the offer expires.</div>
      ) : null}

      {/* Only if — the gap-specific box (§6) */}
      {step.onlyIf !== undefined ? (
        <div className="mt-3 rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Only if</div>
          <div className="mt-2 flex flex-col gap-2">
            {step.onlyIf.noReplyToText !== undefined ? (
              <label className="flex items-center gap-2 text-[12.5px] text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  checked={step.onlyIf.noReplyToText}
                  onChange={(e) => { onChange({ ...step, onlyIf: { ...step.onlyIf, noReplyToText: e.target.checked } }); }}
                />
                They haven’t replied to the text
              </label>
            ) : null}
            {step.onlyIf.gapWithinHours !== undefined ? (
              <label className="flex items-center gap-1.5 text-[12.5px] text-[var(--text-secondary)]">
                The gap starts within
                <input
                  type="number"
                  min={1}
                  value={step.onlyIf.gapWithinHours}
                  onChange={(e) => { onChange({ ...step, onlyIf: { ...step.onlyIf, gapWithinHours: Number(e.target.value) } }); }}
                  className="h-7 w-[52px] rounded-[8px] border border-[var(--border-default)] px-2 text-center font-[var(--font-mono)] text-[12.5px] outline-none focus:border-[var(--brand-indigo)]"
                />
                hours
              </label>
            ) : null}
            {step.onlyIf.noMobile !== undefined ? (
              <div className="text-[12px] text-[var(--text-muted)]">Sent instead of the text when there’s no mobile.</div>
            ) : null}
          </div>
        </div>
      ) : null}

      {isCall ? (
        <>
          <div className="mt-3 text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">What should it say?</div>
          <textarea
            value={step.brief ?? ''}
            onChange={(e) => { onChange({ ...step, brief: e.target.value }); }}
            rows={3}
            className="mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[13.5px] leading-[1.7] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          />
          {/* If the time has gone — locked info (§6) */}
          <div className="mt-3 rounded-[12px] bg-[#FBF3E4] px-3.5 py-2.5">
            <div className="text-[12px] font-bold text-[var(--warn-amber)]">If the time has gone</div>
            <div className="mt-0.5 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              Before booking, the call checks the time is still free. If someone has just taken it, ELVA says
              “I’m sorry, that time has just been taken — would you like me to find you another?”
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mt-3 text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Message</div>
          <textarea
            value={step.body}
            onChange={(e) => { onChange({ ...step, body: e.target.value }); }}
            rows={3}
            className="mt-1.5 w-full rounded-[10px] border border-[var(--border-default)] px-3.5 py-2.5 text-[13.5px] leading-[1.7] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
          />
          {step.channel === 'text' ? (
            <div className="mt-1.5 rounded-[9px] bg-[var(--brand-indigo-tint)] px-3 py-2 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
              <b className="font-semibold">Reply YES to book it. First to reply gets it.</b> is added to the end for you.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export type { IconKind };
